import React, { useState, useEffect, useCallback, use } from "react";
import { toast } from "../../utils/notify";
import {
  getFrameworkMasterListApi,
  getCustomerFrameworkListApi,
  createFrameworkMasterApi,
  updateFrameworkMasterApi,
  deleteFrameworkMasterApi,
  getFrameworkMasterControlsApi,
  getCustomerFrameworkControlsApi,
  createFrameworkMasterControlApi,
  updateFrameworkMasterControlApi,
  deleteFrameworkMasterControlApi,
  assignSelectedControlsToCustomerApi,
  updateCustomerFrameworkMasterControlApi,
  getTrustCenterFrameworksEmployeeApi,
  getAssignedEmpControlsApi,
} from "../../services/axios/AuthApis/TrustCenterApis";
import FrameworkTable from "./frameworkMaster/FrameworkTable";
import ControlsView from "./frameworkMaster/ControlsView";
import { useAuth } from "../../context/AuthContext";


/**
 * FrameworkMasterManager
 * ──────────────────────
 * Thin orchestrator: owns all state & API calls, delegates rendering to
 *   • FrameworkTable  – list + CRUD for FrameworkMaster
 *   • ControlsView    – drill-down list + CRUD for ControlMaster
 */
const FrameworkMasterManager = ({
  sourceType = "framework-master",
  customerId = null,
  canManageFrameworks = true,
  canManageControls = true,
}) => {
  // ── Frameworks ──────────────────────────────────────────────────────────
  const [frameworks, setFrameworks] = useState([]);
  const [fwLoading, setFwLoading] = useState(true);

  // ── Controls drill-down ──────────────────────────────────────────────────
  const [selectedFw, setSelectedFw] = useState(null);
  const [controls, setControls] = useState([]);
  const [ctrlLoading, setCtrlLoading] = useState(false);
  const [ctrlPagination, setCtrlPagination] = useState({
    page: 1, pageSize: 50, total: 0, totalPages: 1,
  });

  const {user} = useAuth();  
  const normalizeFramework = (fw = {}) => {
    const id = fw.id || fw._id || fw.framework_id || null;
    const hasEntitlementStatus = typeof fw.status === "boolean";
    const enabled = typeof fw.enabled === "boolean"
      ? fw.enabled
      : (sourceType === "customer-framework" && hasEntitlementStatus
        ? fw.status
        : sourceType !== "customer-framework");
    const statusText = String(fw.status || "").toLowerCase();
    const isActiveFromStatus = statusText ? statusText === "active" : undefined;
    return {
      ...fw,
      id,
      framework_id: fw.framework_id || id,
      name: fw.name || "Unnamed Framework",
      description: fw.description || "",
      country_or_origin: fw.country_or_origin || fw.country || "",
      framework_type: fw.framework_type || fw.type || "",
      controls_count: Number(fw.controls_count || 0),
      acknowledged_count: Number(fw.acknowledged_count || 0),
      acknowledge_average: Number(fw.acknowledge_average || 0),
      enabled,
      is_active:
        typeof fw.is_active === "boolean"
          ? fw.is_active
          : (typeof isActiveFromStatus === "boolean" ? isActiveFromStatus : true),
    };
  };

  // ── Load all frameworks ──────────────────────────────────────────────────
  const loadFrameworks = useCallback(async () => {
    setFwLoading(true);
    try {
      if (sourceType === "customer-framework") {
         if(user?.role == "end_user"){
          const d = await getTrustCenterFrameworksEmployeeApi();
          const list = Array.isArray(d?.framework) ? d.framework : [];
          setFrameworks(list.map(normalizeFramework));
        }else{
          if (!customerId) {
            setFrameworks([]);
            return;
          }
          const d = await getCustomerFrameworkListApi(customerId);
          const list = Array.isArray(d?.framework) ? d.framework : [];
          setFrameworks(list.map(normalizeFramework));
        }
      } else {
        const d = await getFrameworkMasterListApi();
        const list = Array.isArray(d?.frameworks) ? d.frameworks : [];
        setFrameworks(list.map(normalizeFramework));
      }
    } catch {
      toast.error("Failed to load frameworks");
    } finally {
      setFwLoading(false);
    }
  }, [sourceType, customerId]);

  useEffect(() => { loadFrameworks(); }, [loadFrameworks]);

  // ── Load controls for the selected framework ─────────────────────────────
  const loadControls = useCallback(async (page, pageSize, search) => {
    if (!selectedFw) return;
    setCtrlLoading(true);
    try {
      if(user?.role == "end_user"){
        const d = await getAssignedEmpControlsApi(selectedFw.customer_framework_id, 1, 50, "")
        setControls(d.results || []);
        setCtrlPagination({
          page: d.page, pageSize: 50, total: d.total, totalPages: d.total_pages,
       });
      }else{
        const d = sourceType === "customer-framework"
          ? await getCustomerFrameworkControlsApi(selectedFw.customer_framework_id, 1, 50, "")
          : await getFrameworkMasterControlsApi(selectedFw.id, 1, 50, "");
        setControls(d.controls || []);
        setCtrlPagination({
          page: d.page, pageSize: d.page_size, total: d.total, totalPages: d.total_pages,
        });
      }
    } catch {
      toast.error("Failed to load controls");
    } finally {
      setCtrlLoading(false);
    }
  }, [selectedFw, sourceType]);

  // ── Open / close drill-down ──────────────────────────────────────────────
  const handleOpenFramework = async (fw) => {
    setSelectedFw(fw);
    setControls([]);
    setCtrlPagination({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
    setCtrlLoading(true);
    try {
      if(user?.role == "end_user"){
        const d = await getAssignedEmpControlsApi(fw.customer_framework_id, 1, 50, "")
        setControls(d.results || []);
        setCtrlPagination({
          page: d.page, pageSize: 50, total: d.total, totalPages: d.total_pages,
       });
      }else{
        const d = sourceType === "customer-framework"
          ? await getCustomerFrameworkControlsApi(fw.customer_framework_id, 1, 50, "")
          : await getFrameworkMasterControlsApi(fw.id, 1, 50, "");
        setControls(d.controls || []);
        setCtrlPagination({
          page: d.page, pageSize: d.page_size, total: d.total, totalPages: d.total_pages,
        });
      }
    } catch {
      toast.error("Failed to load controls");
    } finally {
      setCtrlLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedFw(null);
    setControls([]);
  };

  // ── Framework CRUD ───────────────────────────────────────────────────────
  const handleSaveFramework = async (form) => {
    if (form.id) {
      await updateFrameworkMasterApi(form.id, form);
      toast.success("Framework updated");
    } else {
      await createFrameworkMasterApi(form);
      toast.success("Framework created");
    }
    await loadFrameworks();
  };

  const handleDeleteFramework = async (id) => {
    try {
      await deleteFrameworkMasterApi(id);
      toast.success("Framework deleted");
      await loadFrameworks();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Delete failed");
    }
  };

  // ── Control CRUD ─────────────────────────────────────────────────────────
  const handleSaveControl = async (form) => {
    try{
    const payload = { ...form, framework_id: selectedFw.id };
    if (form.id) {
      const role = user?.role;
      if(role =="customer_admin" || role =="security_analyst"){
        payload.controlId = form.id;
        payload.customerId = customerId;
        await updateCustomerFrameworkMasterControlApi( payload);
      }else{
        await updateFrameworkMasterControlApi(form.id, payload);
      }
      toast.success("Control updated");
    } else {
      await createFrameworkMasterControlApi(selectedFw.id, payload);
      toast.success("Control created");
    }
    await loadControls(ctrlPagination.page, ctrlPagination.pageSize, "");
    loadFrameworks(); // refresh controls_count badge
  } catch (err) {
    console.error("Error saving control:", err);
    toast.error(err?.response?.data?.error || "Save failed"); 
  }
  };

  const handleDeleteControl = async (id) => {
    try {
      await deleteFrameworkMasterControlApi(id);
      toast.success("Control deleted");
      await loadControls(ctrlPagination.page, ctrlPagination.pageSize, "");
      loadFrameworks();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Delete failed");
    }
  };

  const handleAssignSelectedControls = async ({ controlIds, employeeId, dueDate }) => {
    if (sourceType !== "customer-framework") {
      toast.error("Bulk assignment is available only in customer framework view");
      return;
    }
    if (!employeeId) {
      toast.error("Employee is not selected for assignment");
      return;
    }
    if (!controlIds?.length) {
      toast.error("Select at least one control");
      return;
    }
    if (!dueDate) {
      toast.error("Due date is required");
      return;
    }

    try {
      const response = await assignSelectedControlsToCustomerApi({
        employeeId: employeeId,
        controlIds,
        dueDate,
      });
      if (response?.success) {
        toast.success(response?.message || "Controls assigned successfully");
      } else {
        toast.error(response?.message || "Failed to assign controls");
      }
      await loadControls(ctrlPagination.page, ctrlPagination.pageSize, "");
      await loadFrameworks();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to assign controls");
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (selectedFw) {
    return (
      <ControlsView
        framework={selectedFw}
        controls={controls}
        loading={ctrlLoading}
        pagination={ctrlPagination}
        canManageControls={canManageControls}
        sourceType={sourceType}
        onBack={handleBack}
        onLoadControls={loadControls}
        onSaveControl={handleSaveControl}
        onDeleteControl={handleDeleteControl}
        onAssignSelectedControls={handleAssignSelectedControls}
      />
    );
  }

  return (
    <FrameworkTable
      frameworks={frameworks}
      loading={fwLoading}
      canManageFrameworks={canManageFrameworks}
      onOpenFramework={handleOpenFramework}
      onSave={handleSaveFramework}
      onDelete={handleDeleteFramework}
      userRole={user?.role}
    />
  );
};

export default FrameworkMasterManager;
